/**
 * Loading, empty and error states (§33, §38, §39, §49).
 *
 * Three rules from the spec shape these:
 *
 *   §33 "Avoid global blocking spinners for ordinary requests." So the
 *   skeleton is a component you place where the content will be, not a
 *   full-screen overlay.
 *
 *   §49 Every empty state names a next action. "No data" is a dead end; "Pick
 *   a subject and let's learn something new" is not.
 *
 *   §38 "Never show stack traces, SQL/provider errors, internal IDs or
 *   secrets." ErrorState renders `ApiError.displayMessage()` and nothing
 *   else — there is no prop for a raw message, so there is no way to leak one.
 */

import type { ReactNode } from 'react';

import { ApiError, toApiError } from '@/api/errors';
import { useFlag } from '@/features/flags/FlagProvider';
import { useLanguage } from '@/features/language/LanguageProvider';
import { cn } from '@/lib/cn';
import { Button } from './Button';

export function Skeleton({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        'animate-pulse rounded-[var(--radius-lg)] bg-[var(--color-surface-sunken)]',
        className,
      )}
      // A skeleton is decoration. A screen reader should hear the live region
      // that accompanies it, not a stack of empty boxes.
      aria-hidden="true"
    />
  );
}

export function CardSkeleton() {
  return (
    <div className="flex items-center gap-[var(--space-3)] rounded-[var(--radius-xl)] border border-[var(--color-border)] bg-[var(--color-surface)] p-[var(--space-4)]">
      <Skeleton className="h-12 w-12 rounded-[var(--radius-lg)]" />
      <div className="flex flex-1 flex-col gap-2">
        <Skeleton className="h-4 w-1/2" />
        <Skeleton className="h-3 w-3/4" />
      </div>
    </div>
  );
}

/**
 * Announces loading to assistive technology without a visual spinner.
 * §36: "Screen-reader friendly titles/status."
 */
export function LoadingAnnouncement({ label }: { label?: string }) {
  const { t } = useLanguage();
  return (
    <span role="status" aria-live="polite" className="sr-only">
      {label ?? t('common.loading')}
    </span>
  );
}

export function EmptyState({
  title,
  body,
  action,
  illustration,
}: {
  title: string;
  body?: string;
  action?: ReactNode;
  illustration?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-[var(--space-3)] px-[var(--space-4)] py-[var(--space-8)] text-center">
      {illustration ? <div aria-hidden="true">{illustration}</div> : null}
      <p className="text-[length:var(--text-lg)] font-[var(--weight-semibold)] text-[var(--color-text)]">
        {title}
      </p>
      {body ? (
        <p className="max-w-sm text-[length:var(--text-sm)] text-[var(--color-text-muted)]">
          {body}
        </p>
      ) : null}
      {action}
    </div>
  );
}

export function ErrorState({
  error,
  onRetry,
  compact = false,
}: {
  error: unknown;
  onRetry?: () => void;
  compact?: boolean;
}) {
  const { t, language } = useLanguage();
  const paywall = useFlag('SUBSCRIPTION_PAYWALL');
  const apiError: ApiError = toApiError(error);

  /*
   * A trial that has run out is not an error, and it is the one failure in
   * this product with an obvious next step.
   *
   * Handled here rather than on each screen because the 402 can come from
   * homework, practice help or Seekho, and three copies of this would be
   * three places to forget. Before it existed the child got the message and
   * a Retry button that was guaranteed to fail again -- a dead end dressed
   * up as a recoverable problem.
   *
   * Behind SUBSCRIPTION_PAYWALL so the door can be closed again without a
   * rollback: with the flag off the copy still explains what happened, which
   * is what it did before and is no worse than it was.
   *
   * A plain anchor, not `useNavigate`. This lives in `components/ui` and is
   * rendered by things that are not always inside a Router -- an error
   * boundary above one, and every component test that renders it directly.
   * A UI primitive that throws without routing context is a primitive that
   * cannot be used where errors actually happen. The full page load costs a
   * moment on a screen nobody reaches twice.
   */
  const blocked = paywall && apiError.code === 'SUBSCRIPTION_REQUIRED';

  return (
    <div
      // `alert` so it is announced immediately: a child who cannot see the
      // screen still needs to know the thing they tapped did not work.
      role="alert"
      className={cn(
        'flex flex-col items-center gap-[var(--space-3)] rounded-[var(--radius-xl)]',
        'border border-[var(--color-danger)]/25 bg-[var(--color-danger-soft)]',
        'px-[var(--space-4)] text-center',
        compact ? 'py-[var(--space-4)]' : 'py-[var(--space-6)]',
      )}
    >
      {/* §7: never colour alone. The icon and the words both carry it. */}
      <span aria-hidden="true" className="text-2xl">
        😕
      </span>
      <p className="text-[length:var(--text-base)] text-[var(--color-text)]">
        {apiError.displayMessage(language)}
      </p>

      {blocked ? (
        <a
          href="/subscribe"
          className={cn(
            'inline-flex min-h-[var(--tap-primary)] items-center justify-center',
            'rounded-[var(--radius-pill)] bg-[var(--color-primary)] px-[var(--space-5)]',
            'font-[var(--weight-semibold)] text-[var(--color-on-primary)]',
            'focus:outline-none focus:shadow-[var(--shadow-focus)]',
          )}
        >
          {t('billing.subscribe')}
        </a>
      ) : onRetry && apiError.canRetry ? (
        <Button variant="secondary" size="md" onClick={onRetry}>
          {t('error.retry')}
        </Button>
      ) : null}

      {/* §38 permits the request id — it is not sensitive, and it is the only
          handle support has. Shown only for genuine server faults. */}
      {apiError.isUnexpected && apiError.requestId ? (
        <p className="text-[length:var(--text-xs)] text-[var(--color-text-subtle)]">
          <span className="font-[var(--font-mono)]">{apiError.requestId.slice(0, 8)}</span>
        </p>
      ) : null}
    </div>
  );
}
